/** Berths (muelles) a scale can occupy, as in Carlos's list. 90A and 90B are anchorages (fondeo). */
export const MUELLES = ['32A', '32B', '32C', '32D', '32E', '32F', '32G', '34B', '90A', '90B'] as const
export type Muelle = (typeof MUELLES)[number]

/** A regular event moves the scale to a muelle; special events mark an operation. */
export const ScaleEventType = {
  PLACE: 'PLACE',
  MARPOL: 'MARPOL',
  BOMBEO: 'BOMBEO',
} as const
export type ScaleEventType = (typeof ScaleEventType)[keyof typeof ScaleEventType]

export const SPECIAL_EVENT_TYPES = [ScaleEventType.MARPOL, ScaleEventType.BOMBEO] as const
export type SpecialEventType = (typeof SPECIAL_EVENT_TYPES)[number]

/** The special events users report and edit: BOMBEO only. MARPOL events recorded earlier stay, read-only. */
export const REPORTED_EVENT_TYPES = [ScaleEventType.BOMBEO] as const
export type ReportedEventType = (typeof REPORTED_EVENT_TYPES)[number]

interface ScaleEventBase {
  id: string
  scaleId: string
  /** ISO-8601 timestamp. */
  startTime: string
  /** ISO-8601 timestamp; null while the event is still open. */
  endTime: string | null
}

export interface PlaceEvent extends ScaleEventBase {
  type: typeof ScaleEventType.PLACE
  muelle: Muelle
}

export interface SpecialEvent extends ScaleEventBase {
  type: SpecialEventType
}

export type ScaleEvent = PlaceEvent | SpecialEvent

export interface Scale {
  id: string
  /** Created by hand in the app (FAB) rather than coming from the berthing source. */
  isManual?: boolean
  /** A manual scale the source has since reported too; set by {@link mergeManualScale}. */
  seenInSource?: boolean
}

/** Carlos's product classes. SOSTRAT's "ACEITES Y GRASAS" has none until it is decided. */
export const PRODUCT_CLASSES = ['HYDROCARBON', 'CHEMICAL'] as const
export type ProductClass = (typeof PRODUCT_CLASSES)[number]

/** Tipus Operació: Embarque = load (carga), Desembarque = unload (descarga). */
export const OPERATION_KINDS = ['LOAD', 'UNLOAD'] as const
export type OperationKind = (typeof OPERATION_KINDS)[number]

/**
 * How a stated SOSTRAT quantity was read: tonnes, kilograms (most of them,
 * despite the "Toneladas" unit), or implausible either way.
 */
export type QuantityBasis = 'TONNES' | 'KG' | 'IMPLAUSIBLE'

/** One cargo operation SOSTRAT reports for a berth stay (V_TC_ATRAQUEOPERACIO). */
export interface StayOperation {
  /** Estat Atracada, e.g. "Finalizado"; "Anulado" when cancelled. */
  state: string | null
  /** Tipus Operació as given ("Embarque", "Desembarque"). */
  operation: string | null
  kind: OperationKind | null
  /** Mercaderia: the goods category, e.g. "GASOLINA". */
  goods: string | null
  productClass: ProductClass | null
  /** Quantitat and Unitats as given. */
  quantity: number | null
  unit: string | null
  /** The quantity in tonnes; null when implausible or missing. */
  quantityT: number | null
  quantityBasis: QuantityBasis | null
  estibador: string | null
  /** Terminal code for a known Estibador (e.g. TEPSA), else the Estibador. */
  terminal: string | null
  /** The declared operation window (Inici/Final); not the pumping time. */
  windowStart: string | null
  windowEnd: string | null
}

/** What EQ records on a BOMBEO event (from the CIDO and the annex, or on site). */
export interface BombeoDetails {
  /** CÓDIGO DE CONEXIÓN: the connection point (arm or pipeline) used. */
  connectionPoint: string | null
  productClass: ProductClass | null
  /** The exact product, when known. */
  product: string | null
  /** CANTIDAD (TN). */
  quantityT: number | null
  operationKind: OperationKind | null
}
